import React, { useState, useEffect, useMemo } from 'react';
import { getForecastScreen } from '../api';

/**
 * Вкладка «Акції» сторінки «Прогнози»: усі папери переліку на вибрані дату й
 * годину — наше «Резюме» 1h і 1d (як рахує investing.com), голоси індикаторів
 * і середніх, консенсус аналітиків Yahoo.
 *
 * Сервер віддає ~4.7 тис. рядків одним запитом; фільтри, сортування і сторінки —
 * тут. Міні-графіки малюються лише для видимої сторінки (100 рядків).
 */

export const SUMMARY = {
  2:  { label: 'Активно купувати',  color: '#007A41', bg: '#E7F7EF' },
  1:  { label: 'Купувати',          color: '#15803D', bg: '#DCFCE7' },
  0:  { label: 'Нейтрально',        color: '#5B646F', bg: '#F1F3F5' },
  '-1': { label: 'Продавати',       color: '#C2410C', bg: '#FFEDD5' },
  '-2': { label: 'Активно продавати', color: '#C02B5E', bg: '#FDE8EF' },
};

const HOURS = [10, 11, 12, 13, 14, 15, 16];
const PAGE = 100;

const COLS = [
  { key: 't',         label: 'Тікер',        align: 'left'  },
  { key: 'name',      label: 'Назва',        align: 'left'  },
  { key: 'sector',    label: 'Сектор',       align: 'left'  },
  { key: 'cap',       label: 'Кап.',         align: 'right' },
  { key: null,        label: '30 днів',      align: 'left'  },
  { key: 'close',     label: 'Ціна',         align: 'right' },
  { key: 'chg',       label: 'За день',      align: 'right' },
  { key: 'adv30',     label: 'Обіг 30д',     align: 'right' },
  { key: 's1h',       label: 'Резюме 1h',    align: 'left'  },
  { key: 's1d',       label: 'Резюме 1d',    align: 'left'  },
  { key: 'an_upside', label: 'Прогноз росту', align: 'right' },
  { key: 'an_buy',    label: 'Купити/трим./прод.', align: 'right' },
  { key: 'an_target', label: 'Ціль',         align: 'right' },
];

function fmtMoney(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)} трлн`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)} млрд`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)} млн`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)} тис`;
  return `$${v.toFixed(0)}`;
}
const fmtPrice = (v) => (v === null || v === undefined ? '—' : v < 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`);
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(2)}%`);

function Spark({ data }) {
  if (!data || data.length < 2) return null;
  const w = 90, h = 26;
  const lo = Math.min(...data), hi = Math.max(...data);
  const k = hi > lo ? (h - 4) / (hi - lo) : 0;
  const pts = data.map((v, i) => `${(i * w) / (data.length - 1)},${h - 2 - (v - lo) * k}`).join(' ');
  const up = data[data.length - 1] >= data[0];
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={up ? '#00A656' : '#C02B5E'} strokeWidth="1.4" />
    </svg>
  );
}

function SummaryCell({ s, ib, is, mb, stale }) {
  if (s === null || s === undefined) {
    return <span style={{ color: '#8A929C', fontSize: 12 }} title="Мало історії: для денного «Резюме» потрібно 400 днів">—</span>;
  }
  const st = SUMMARY[s];
  return (
    <div>
      <span style={{ ...S.badge, background: st.bg, color: st.color }}>
        {st.label}{stale ? ' ·' : ''}
      </span>
      {ib >= 0 && (
        <div style={S.votes} title="Індикатори: купувати / продавати з 11 (ATR не голосує) · ковзні середні нижче ціни з 12">
          інд. {ib}↑ {is}↓ · MA {mb}/12
        </div>
      )}
    </div>
  );
}

function RangeInput({ label, unit, value, onChange }) {
  return (
    <label style={S.range}>
      <span style={S.rangeLabel}>{label}{unit ? `, ${unit}` : ''}</span>
      <input style={S.num} placeholder="від" value={value[0]}
             onChange={(e) => onChange([e.target.value, value[1]])} />
      <input style={S.num} placeholder="до" value={value[1]}
             onChange={(e) => onChange([value[0], e.target.value])} />
    </label>
  );
}

// '' → без межі; число в заданих одиницях (млн для капіталізації й обігу).
function inRange(v, [lo, hi], mul = 1) {
  const a = lo === '' ? null : Number(lo) * mul;
  const b = hi === '' ? null : Number(hi) * mul;
  if (a === null && b === null) return true;
  if (v === null || v === undefined) return false;
  if (a !== null && !Number.isNaN(a) && v < a) return false;
  if (b !== null && !Number.isNaN(b) && v > b) return false;
  return true;
}

export default function ForecastStocks({ onOpenStock }) {
  const [date,   setDate]   = useState(null);
  const [hour,   setHour]   = useState(16);
  const [data,   setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,  setError]  = useState('');
  const [search, setSearch] = useState('');
  const [sector, setSector] = useState('');
  const [cap,    setCap]    = useState(['', '']);
  const [price,  setPrice]  = useState(['', '']);
  const [adv,    setAdv]    = useState(['', '']);
  const [f1h,    setF1h]    = useState('');
  const [f1d,    setF1d]    = useState('');
  const [sort,   setSort]   = useState({ key: 'cap', dir: 'desc' });
  const [page,   setPage]   = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    getForecastScreen(date, hour)
      .then((res) => { if (alive) { setData(res.data); if (!date) setDate(res.data.date); } })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date, hour]);

  const sectors = useMemo(() => {
    const s = new Set((data?.rows || []).map((r) => r.sector).filter(Boolean));
    return [...s].sort();
  }, [data]);

  const rows = useMemo(() => {
    let out = data?.rows || [];
    const q = search.trim().toUpperCase();
    if (q) out = out.filter((r) => r.t.includes(q) || (r.name || '').toUpperCase().includes(q));
    if (sector === '—') out = out.filter((r) => !r.sector);
    else if (sector) out = out.filter((r) => r.sector === sector);
    if (f1h !== '') out = out.filter((r) => r.s1h === Number(f1h));
    if (f1d !== '') out = out.filter((r) => r.s1d === Number(f1d));
    out = out.filter((r) => inRange(r.cap, cap, 1e6) && inRange(r.close, price)
                          && inRange(r.adv30, adv, 1e6));
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const x = a[key], y = b[key];
      // Порожні значення завжди в кінці — «немає консенсусу» не «найменший прогноз росту».
      if (x === null || x === undefined || x === '') return 1;
      if (y === null || y === undefined || y === '') return -1;
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * mul;
      return String(x).localeCompare(String(y)) * mul;
    });
  }, [data, search, sector, f1h, f1d, cap, price, adv, sort]);

  useEffect(() => { setPage(0); }, [search, sector, f1h, f1d, cap, price, adv, sort, date, hour]);

  const onSort = (key) => key && setSort((s) => (
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  ));
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible = rows.slice(page * PAGE, page * PAGE + PAGE);
  const days = data?.days || [];

  return (
    <div>
      <div style={S.controls}>
        <label style={S.range}>
          <span style={S.rangeLabel}>Дата</span>
          <select style={S.select} value={date || ''} onChange={(e) => setDate(e.target.value)}>
            {[...days].reverse().map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        <label style={S.range}>
          <span style={S.rangeLabel}>Година (Нью-Йорк)</span>
          <select style={S.select} value={hour} onChange={(e) => setHour(Number(e.target.value))}>
            {HOURS.map((h) => <option key={h} value={h}>{h}:00{h === 16 ? ' (закриття)' : ''}</option>)}
          </select>
        </label>
        <label style={S.range}>
          <span style={S.rangeLabel}>Сектор</span>
          <select style={S.select} value={sector} onChange={(e) => setSector(e.target.value)}>
            <option value="">усі</option>
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            <option value="—">без сектора</option>
          </select>
        </label>
        <RangeInput label="Кап." unit="млн $" value={cap} onChange={setCap} />
        <RangeInput label="Ціна" unit="$" value={price} onChange={setPrice} />
        <RangeInput label="Обіг 30д" unit="млн $/день" value={adv} onChange={setAdv} />
        <label style={S.range}>
          <span style={S.rangeLabel}>Резюме 1h</span>
          <select style={S.select} value={f1h} onChange={(e) => setF1h(e.target.value)}>
            <option value="">усі</option>
            {[2, 1, 0, -1, -2].map((v) => <option key={v} value={v}>{SUMMARY[v].label}</option>)}
          </select>
        </label>
        <label style={S.range}>
          <span style={S.rangeLabel}>Резюме 1d</span>
          <select style={S.select} value={f1d} onChange={(e) => setF1d(e.target.value)}>
            <option value="">усі</option>
            {[2, 1, 0, -1, -2].map((v) => <option key={v} value={v}>{SUMMARY[v].label}</option>)}
          </select>
        </label>
        <label style={S.range}>
          <span style={S.rangeLabel}>Пошук</span>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="тікер або назва" style={{ ...S.select, minWidth: 160 }} />
        </label>
      </div>

      {data && (
        <div style={S.meta}>
          Показано {rows.length.toLocaleString('uk-UA')} з {data.rows.length.toLocaleString('uk-UA')} ·
          {' '}Резюме 1h — на барі, що закрився о {data.hour}:00 (позначка «·» — бару цієї години не було,
          стан на попередньому) · Резюме 1d — на закритті {data.hour === 16 ? 'цього' : 'попереднього'} дня ·
          {' '}консенсус аналітиків і капіталізація — Yahoo від {data.analysts_date || '—'}
          {data.analysts_later && <b style={{ color: '#C2410C' }}> (пізніше за вибрану дату: раніше консенсус не збирався)</b>}
          {' '}· таблиця від {data.built_at}
        </div>
      )}
      {error && <div style={S.err}>{error}</div>}
      {loading && <div style={S.info}>Завантаження…</div>}

      {!loading && !error && data && (<>
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {COLS.map((c) => (
                  <th key={c.label} onClick={() => onSort(c.key)}
                      style={{ ...S.th, textAlign: c.align, cursor: c.key ? 'pointer' : 'default' }}>
                    {c.label}{sort.key === c.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.t} style={S.tr}>
                  <td style={{ ...S.td, fontWeight: 600 }}>
                    <span style={S.link} onClick={() => onOpenStock && onOpenStock(r.t, date, hour)}>{r.t}</span>
                  </td>
                  <td style={{ ...S.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={r.name || ''}>{r.name || '—'}</td>
                  <td style={{ ...S.td, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      title={r.industry || ''}>{r.sector || '—'}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtMoney(r.cap)}</td>
                  <td style={S.td}><Spark data={r.spark} /></td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtPrice(r.close)}</td>
                  <td style={{ ...S.td, textAlign: 'right',
                               color: r.chg > 0 ? '#007A41' : r.chg < 0 ? '#C02B5E' : '#1A1A1A' }}>
                    {fmtPct(r.chg)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtMoney(r.adv30)}</td>
                  <td style={S.td}><SummaryCell s={r.s1h} ib={r.ib1h} is={r.is1h} mb={r.mb1h} stale={r.stale_bar} /></td>
                  <td style={S.td}><SummaryCell s={r.s1d} ib={r.ib1d} is={r.is1d} mb={r.mb1d} /></td>
                  <td style={{ ...S.td, textAlign: 'right',
                               color: r.an_upside > 0 ? '#007A41' : r.an_upside < 0 ? '#C02B5E' : '#1A1A1A' }}>
                    {fmtPct(r.an_upside)}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}
                      title={`Рейтинги ${(r.an_buy || 0) + (r.an_hold || 0) + (r.an_sell || 0)} аналітиків · цільових цін ${r.an_n ?? 0}`}>
                    {/* Рейтинги показуємо навіть без цілей: частина аналітиків ціль не публікує. */}
                    {(r.an_buy || r.an_hold || r.an_sell) ? <>
                      <span style={{ color: '#007A41' }}>{r.an_buy}</span> /
                      {' '}<span style={{ color: '#5B646F' }}>{r.an_hold}</span> /
                      {' '}<span style={{ color: '#C02B5E' }}>{r.an_sell}</span>
                    </> : '—'}
                  </td>
                  <td style={{ ...S.td, textAlign: 'right' }}>{fmtPrice(r.an_target)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ ...S.controls, marginTop: 10 }}>
          <button style={S.tab} disabled={page === 0} onClick={() => setPage(page - 1)}>←</button>
          <span style={S.meta}>сторінка {page + 1} з {pages}</span>
          <button style={S.tab} disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>→</button>
        </div>
      </>)}
    </div>
  );
}

const S = {
  controls: { display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 },
  range: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#5B646F' },
  rangeLabel: { fontSize: 12, color: '#5B646F' },
  num:   { width: 70, padding: '6px 8px', borderRadius: 6, border: '1px solid #E5E7EB',
           fontSize: 13, color: '#1A1A1A', background: '#fff', marginRight: 4 },
  select: { padding: '6px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 13,
            color: '#1A1A1A', background: '#fff' },
  meta:  { margin: '0 0 10px', fontSize: 12, color: '#5B646F', lineHeight: 1.5 },
  tab:   { padding: '6px 12px', borderRadius: 6, border: '1px solid #E5E7EB',
           background: '#FFFFFF', color: '#5B646F', cursor: 'pointer', fontSize: 13 },
  tableWrap: { overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: 8, background: '#FFFFFF' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:    { padding: '10px 10px', background: '#F9FAFB', color: '#5B646F', userSelect: 'none',
           whiteSpace: 'nowrap', borderBottom: '1px solid #E5E7EB', fontWeight: 600 },
  tr:    { borderBottom: '1px solid #F1F3F5' },
  td:    { padding: '6px 10px', color: '#1A1A1A', whiteSpace: 'nowrap',
           fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 12, whiteSpace: 'nowrap', fontWeight: 500 },
  votes: { fontSize: 11, color: '#8A929C', marginTop: 2 },
  link:  { color: '#475FFA', cursor: 'pointer' },
  info:  { padding: 16, color: '#5B646F' },
  err:   { padding: 12, background: '#FDE8EF', border: '1px solid #F5C2D3',
           borderRadius: 6, color: '#C02B5E', marginBottom: 12 },
};
